import { usePageHeader } from '@easydoctor/ui';
import { threadEntryAnchor } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useRef } from 'react';
import { useLocation, useOutletContext, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { useTicket } from '../../api/tickets';
import { TicketAssignmentSection } from './TicketAssignmentSection';
import { TicketComposer } from './TicketComposer';
import { TicketContextSection } from './TicketContextSection';
import { TicketCorrectionSection } from './TicketCorrectionSection';
import type { TicketDetailOutletContext } from './TicketDetailDrawer';
import { TicketHeader } from './TicketHeader';
import { TicketPrioritySection } from './TicketPrioritySection';
import { TicketProvenanceSection } from './TicketProvenanceSection';
import { TicketReporterSection } from './TicketReporterSection';
import { TicketStateSection } from './TicketStateSection';
import { anchorFromHash, revealThreadEntry } from './thread-anchor';
import { TicketThread } from './TicketThread';

/**
 * Dettaglio del ticket (PAGE-002), rotta `/ticket/:code` dentro la coda, reso nel `TicketDetailDrawer`
 * che gli passa chiusura, testo del ritorno e percorso. Ogni blocco è un `MainCard` (`DetailSection`) e
 * un componente a sé perché le story di lavorazione ci lavorino in parallelo: #3
 * `TicketPrioritySection` (la priorità si imposta qui), #4 `TicketAssignmentSection`, #5
 * `TicketThread` e `TicketComposer` sotto il thread. Nessun elemento del canale paziente (DEC-005).
 */
export function TicketDetailPanel() {
  const { code = '' } = useParams();
  const { onClose, backLabel, crumb } = useOutletContext<TicketDetailOutletContext>();
  const { data: ticket, isPending, isError, error, refetch, isFetching } = useTicket(code);
  const location = useLocation();
  // Nella shell il percorso è «Coda › EC-2026-0001» e il codice dà il titolo alla scheda del browser,
  // maiuscolo come lo scrive l'API anche se l'URL è in minuscolo (`/ticket/ec-2026-0001`).
  // «Coda» porta dove porta «Torna alla coda», la coda con gli stessi filtri, ma è un link normale:
  // aggiunge una voce alla cronologia, mentre «Torna alla coda» chiude il dettaglio con `replace`.
  const displayCode = ticket?.code ?? code.trim().toUpperCase();
  usePageHeader({
    breadcrumb: [crumb, { label: displayCode }],
    title: displayCode,
  });
  const titleRef = useRef<HTMLHeadingElement>(null);

  const loadedCode = ticket?.code;
  const anchor = anchorFromHash(location.hash);
  const landsOnEntry = Boolean(anchor && ticket?.thread.some((entry) => threadEntryAnchor(entry) === anchor));

  // Aprendo un ticket il focus va al titolo, così tastiera e lettori di schermo lo seguono; una
  // volta sola per ticket: l'ancora che sparisce dall'URL (ricerca e filtri della coda riscrivono
  // la query string) non lo riporta al titolo mentre si scrive altrove.
  const focusedCode = useRef<string | null>(null);
  useEffect(() => {
    if (!loadedCode || focusedCode.current === loadedCode) return;
    focusedCode.current = loadedCode;
    if (!landsOnEntry) titleRef.current?.focus();
  }, [loadedCode, landsOnEntry]);

  // Con l'ancora di una voce del thread (link delle notifiche, #70) il focus va a quella voce,
  // evidenziata: all'apertura e a ogni nuova ancora, mai quando l'ancora si toglie.
  useEffect(() => {
    if (!loadedCode || !anchor) return;
    return revealThreadEntry(anchor) ?? undefined;
  }, [loadedCode, anchor]);

  if (isPending) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" role="status" sx={{ p: 6 }}>
        <CircularProgress size={20} />
        <Typography>Caricamento del ticket {code}…</Typography>
      </Stack>
    );
  }

  if (isError) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <Box sx={{ p: 6 }}>
        <Alert
          severity={notFound ? 'warning' : 'error'}
          action={
            notFound ? (
              <Button color="inherit" size="small" onClick={onClose}>
                {backLabel}
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => void refetch()} disabled={isFetching}>
                Riprova
              </Button>
            )
          }
        >
          <AlertTitle>{notFound ? 'Ticket non trovato' : 'Ticket non disponibile'}</AlertTitle>
          {error.message}
        </Alert>
      </Box>
    );
  }

  // Su `grey.50` del pannello: la testata senza cornice, poi un blocco per card, come nel prototipo.
  return (
    <Stack spacing={4} sx={{ px: { xs: 4, sm: 6 }, pt: 5, pb: 10 }}>
      <TicketHeader titleRef={titleRef} ticket={ticket} />
      {/* `key`: esito ed errore del cambio di priorità restano del ticket su cui sono avvenuti. */}
      <TicketPrioritySection key={ticket.code} ticket={ticket} />
      <TicketAssignmentSection ticket={ticket} />
      {/* #6: cambio di stato, «Scarta», «Chiudi» e «Riapri»; la correzione sta sopra il thread. */}
      <TicketStateSection key={`state:${ticket.code}`} ticket={ticket} />
      <TicketReporterSection ticket={ticket} />
      <TicketProvenanceSection ticket={ticket} />
      <TicketContextSection ticket={ticket} />
      <TicketCorrectionSection key={`correction:${ticket.code}`} ticket={ticket} />
      {/* L'ancora apre il gruppo di eventi che contiene la voce (#120); un thread per ticket. */}
      <TicketThread key={`thread:${ticket.code}`} entries={ticket.thread} code={ticket.code} anchor={anchor} />
      {/* Una bozza per ticket: passando a un altro ticket il composer riparte vuoto. */}
      <TicketComposer key={`composer:${ticket.code}`} ticket={ticket} />
    </Stack>
  );
}
